/* eslint no-param-reassign: 0 */

import 'whatwg-fetch';
import { replace } from 'react-router-redux';
import Auth from '@aws-amplify/auth';
import API from '@aws-amplify/api';
import clone from 'clone';
import Cookies from 'js-cookie';

import {
  FETCH_REQUEST,
  FETCH_FAILURE,
  FETCH_SUCCESS,

  NETWORK_FAILURE,
  NETWORK_OK,
} from '../constants/actions';
import config from '../../config';
import { getStateAdfsProvider } from '../selectors';
import { FetchError } from '../utils/error';
import { handleSessionLost, isSessionSuperseded } from '../utils/session';
import { attachNetworkDiagnostics } from '../utils/networkDiagnostics';
import { flashErrorMessage } from './flash';
import { redirectToLogin, giveLoginFromADFS } from './auth';

function fetchRequest() {
  return {
    type: FETCH_REQUEST,
  };
}

function fetchError(message) {
  return {
    type: FETCH_FAILURE,
    payload: {
      statusText: message,
    },
  };
}

function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  };
}

function networkOK() {
  return {
    type: NETWORK_OK,
  };
}

function networkError() {
  return {
    type: NETWORK_FAILURE,
  };
}

async function getJSONfromResponse(res) {
  try {
    const data = await res.json();
    return data;
  } catch (e) {
    return null;
  }
}


// scheme://host[:port] of a URL, lower-cased, with userinfo forms rejected (returns ''). Kept as
// a regex rather than `new URL` — this bundle still targets browsers without a URL polyfill.
function urlOrigin(u) {
  const m = /^(https?):\/\/([^/?#]*)/i.exec(u || '');
  if (!m || m[2].indexOf('@') !== -1 || m[2] === '') return '';
  return `${m[1].toLowerCase()}://${m[2].toLowerCase()}`;
}

export function fetchM(u, opts = {}, retry = true) {
  const currentVersion = config.get('version') || '';
  let user;
  let adfsProvider = '';
  return async (dispatch) => {
    await dispatch(fetchRequest());
    try {
      user = await Auth.currentAuthenticatedUser();
    } catch (error) {
      if (
        retry && (
          (typeof error === 'string' && !error.match(/not authenticate/i)) || (
            typeof error === 'object' && error.code !== 'PasswordResetRequiredException'
            && (error.message.match(/not authenticate/i)
              || error.message.match(/token/i))
          )
        )
      ) {
        // delay retry to check new token
        return dispatch(fetchM(u, opts, false));
      }
      await dispatch(redirectToLogin(window.location.pathname));
      return Promise.reject(error);
    }

    if (user) {
      const attributes = user.getSignInUserSession().getIdToken().decodePayload();
      if (attributes) {
        let { identities } = attributes;
        if (identities) {
          if (typeof identities === 'string') {
            identities = JSON.parse(identities);
          }
          if (Array.isArray(identities)) {
            adfsProvider = identities.reduce((result, identity) => {
              if (result === '' && identity.providerName) {
                return identity.providerName;
              }
              return result;
            }, adfsProvider);
          }
        }
      }
    }

    const stateAdfsProvider = getStateAdfsProvider();
    if (adfsProvider && adfsProvider !== stateAdfsProvider) {
      dispatch(giveLoginFromADFS(adfsProvider));
    }

    let url = u;
    if (!/^(f|ht)tps?:\/\//i.test(url)) {
      url = `${config.get('bookApiURL')}${url}`;
    }
    const headers = opts.headers ? clone(opts.headers) : {};
    let origin = window.location.origin;
    if (!origin) {
      origin = `${window.location.protocol}//${window.location.hostname}`;
      origin += `${(window.location.port ? `:${window.location.port}` : '')}`;
    }
    headers['X-Origin'] = origin;
    headers['X-Showroom-Version'] = currentVersion || '';

    // Send the CURRENT session's id token explicitly on book-api calls. Without it, book-api's
    // cookie fallback GUESSES the caller: it scans its configured Cognito app-client ids and takes
    // the first LastAuthUser cookie it finds — and on the shared .mhub.my cookie domain a browser
    // that has used other MHub apps carries several apps' cookie sets, so book-api can end up
    // validating a STALE token this app doesn't own and answer 428 token_expired (the retry below
    // has been papering over exactly that). book-api's middleware prefers x-aws-id-token over any
    // cookie, and the header is already in its CORS allowlist. currentSession() refreshes an
    // expired token, so the header always carries live material; when it can't (signed out,
    // devstack persona mode), the header is absent and the cookie/persona path applies as before.
    //
    // The gate is an ORIGIN comparison, never a string prefix: a prefix check accepts lookalike
    // hosts ("https://book-api.x.evil.example", "https://book-api.x@evil.example") and would hand
    // the token to a foreign origin. Userinfo forms are rejected outright — no book-api URL ever
    // carries one. The header is REWRITTEN on every attempt: fetchM's 428 retry re-enters with the
    // same opts object, so a token attached on the first attempt must not be replayed stale on the
    // second (a leftover from a now-dead session is deleted rather than sent).
    if (urlOrigin(url) && urlOrigin(url) === urlOrigin(config.get('bookApiURL') || '')) {
      try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        if (idToken) {
          headers['x-aws-id-token'] = idToken;
        } else {
          delete headers['x-aws-id-token'];
        }
      } catch (e) {
        delete headers['x-aws-id-token'];
      }
    }
    opts.headers = headers;

    let res;
    // Read before the request goes out: it is what tells this attempt's Resource Timing entry
    // apart from an earlier request to the same URL (networkDiagnostics.attemptWasSent).
    const startedAt = typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now() : null;
    try {
      res = await fetch(url, opts);
    } catch (err) {
      // MS-6583: a network-level rejection names neither its request nor its cause. Ride the
      // facts on the error for the one event bugsnagNetworkNoise lets through — sizes only,
      // never the token itself. The original message is recorded here because a caller's
      // catch (actions/error.js handleError) may rename it before Bugsnag ever sees it.
      attachNetworkDiagnostics(err, {
        url,
        method: opts.method,
        retry: !retry,
        message: err && err.message,
        startedAt,
        tokenHeaderBytes: headers['x-aws-id-token'] ? headers['x-aws-id-token'].length : 0,
      });
      if (err.message.match(/failed to fetch/i)) {
        await dispatch(networkError());
      } else if (retry) {
        return dispatch(fetchM(u, opts, false));
      }
      await dispatch(fetchError(err.message));
      return Promise.reject(err);
    }

    await dispatch(networkOK());
    if (!res.ok) {
      let message = 'An error occurred';
      if (res.status === 401) {
        // MS-6466: a superseded session answers 401 with the contract body. It must not fall
        // into redirectToLogin — the id token is still valid, so the login app would bounce
        // straight back here in a loop. handleSessionLost hands off to /logout with the
        // reason (one navigation, however many fetches fail together).
        let bodyText = '';
        try {
          bodyText = await res.clone().text();
        } catch (e) {
          // an unreadable body is not a takeover verdict
        }
        if (isSessionSuperseded(res.status, bodyText)) {
          handleSessionLost();
          await dispatch(fetchError('Session taken over'));
          return Promise.reject(new FetchError('Session taken over', res));
        }
        await dispatch(redirectToLogin(window.location.pathname));
      }
      if (res.status === 403 && opts.returnForbidden) {
        // opts.returnForbidden, like opts.ignoreNotFound below: some endpoints answer 403 as part
        // of their contract rather than as "you should not be here", and their caller has better
        // copy to show than the generic forbidden page; the response is handed back as a value.
        // CRM link sessions use it: a session opened by a colleague is a 403 the salesperson needs
        // explained, not a dead end (MS-6620). opts.ignoreForbidden (CPI-1347) is the other shape:
        // no redirect, but the call still rejects with the response.
        await dispatch(fetchSuccess());
        return res;
      }
      if (res.status === 403 && !opts.ignoreForbidden) {
        // TODO: way to reset checkChanges hasChanges
        await dispatch(replace('/forbidden/'));
        message = 'Access denied';
        await dispatch(flashErrorMessage({ message }));
      }
      if (res.status === 404) {
        if (opts.ignoreNotFound) {
          await dispatch(fetchSuccess());
          return res;
        }
        await dispatch(replace('/not_found/'));
        message = 'Not found';
        await dispatch(flashErrorMessage({ message }));
      }
      if (res.status === 428 && retry) {
        return dispatch(fetchM(u, opts, false));
      }
      await dispatch(fetchError(message));
      return Promise.reject(new FetchError(message, res));
    }
    await dispatch(fetchSuccess());

    // app version checking, auto reload if there is a newer version
    const versionCheck = config.get('versionCheck');
    const xShowroomMinVersion = res.headers.get('x-showroom-min-version');
    // eslint-disable-next-line max-len
    if (res.ok && xShowroomMinVersion && currentVersion && navigator.cookieEnabled && versionCheck) {
      let cookie = Cookies.getJSON('mhub-book-web-version-checker');
      // setup cookie if it does not exists
      if (!cookie) {
        Cookies.set('mhub-book-web-version-checker', { minVersion: 0, reloadCount: 0 });
        cookie = { minVersion: 0, reloadCount: 0 };
      }
      let reloadCountExceeded = false;
      let minVersion = `${xShowroomMinVersion.replace(/\D/g, '')}0000000000`;
      let version = `${currentVersion.replace(/\D/g, '')}0000000000`;
      minVersion = parseInt(minVersion.substring(0, 10), 10);
      version = parseInt(version.substring(0, 10), 10);
      // check if reload count exceeded
      if (cookie && cookie.reloadCount > 2) {
        reloadCountExceeded = true;
      }
      // reload if min version is more than currenct version and not exceeded the reload count
      if (minVersion > version && !reloadCountExceeded) {
        location.reload(true);
        Cookies.set('mhub-book-web-version-checker', { minVersion, reloadCount: cookie.reloadCount + 1 });
      } else if (cookie && cookie.minVersion === version) {
        Cookies.set('mhub-book-web-version-checker', { minVersion, reloadCount: 0 });
      }
    }

    return res;
  };
}

export function fetchAPI(api, path = '/', body, method = 'get', opts = {}) {
  const options = {
    headers: opts,
    body,
  };
  return (dispatch) => {
    dispatch(fetchRequest());
    return API[method](api, path, options)
      .then((res) => {
        dispatch(networkOK());
        dispatch(fetchSuccess());
        return Promise.resolve(res);
      })
      .catch((err) => {
        const errorMessage = err.response && err.response.data && err.response.data.error_message;
        if (errorMessage && errorMessage.match(/failed to fetch/i)) {
          dispatch(networkError());
        }
        dispatch(fetchError(errorMessage || 'An error occurred'));
        return Promise.reject(err);
      });
  };
}

// Send a network request to server, default method is GET
function serverRequest(apiUrl, store, httpOpts = {}) {
  if (!store || !apiUrl) return Promise.reject('Required parameters missing.');
  if (!httpOpts.method) {
    // Set default
    httpOpts = {
      credentials: 'include',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };
  }

  return store.dispatch(fetchM(apiUrl, httpOpts));
}
// TODO: refactor to options params instead of multiple params
// Send request to server accoring to options provided.
// Handle response from server accordingly and return response data
async function execServerRequest(apiUrl, store, httpOpts = {}) {
  let res;
  try {
    res = await serverRequest(apiUrl, store, httpOpts);
    if (!res.ok) {
      const message = await res.text();
      return {
        error: message,
      };
    }
    const token = res.headers.get('X-Csrf-Token');
    const count = parseInt(res.headers.get('M-Data-count'), 10);
    const limit = parseInt(res.headers.get('M-Data-limit'), 10);
    const offset = parseInt(res.headers.get('M-Data-offset'), 10);
    const data = await getJSONfromResponse(res);
    return {
      count,
      data,
      error: false,
      limit,
      offset,
      token,
    };
  } catch (e) {
    // MS-6583: rethrow as-is. `new Error(e)` forked every rejection into a second Bugsnag
    // group ("Error: TypeError: Failed to fetch") and destroyed FetchError's `.res`, so
    // callers' `err.res` branches never fired on this path.
    throw e;
  }
}

export function serverRequestGet(apiUrl, store) {
  return execServerRequest(apiUrl, store);
}

export function serverRequestPost(apiUrl, store, token, body, overrideHttpOpts = null) {
  let httpOpts = {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Csrf-Token': token,
    },
    body: JSON.stringify(body),
  };
  if (overrideHttpOpts) httpOpts = overrideHttpOpts;
  return execServerRequest(apiUrl, store, httpOpts);
}

export function serverRequestPut(apiUrl, store, token, body) {
  const httpOpts = {
    credentials: 'include',
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Csrf-Token': token,
    },
    body: JSON.stringify(body),
  };
  return execServerRequest(apiUrl, store, httpOpts);
}

export function serverRequestDelete(apiUrl, store, token, body = {}) {
  const httpOpts = {
    credentials: 'include',
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-Csrf-Token': token,
    },
    body: JSON.stringify(body),
  };
  return execServerRequest(apiUrl, store, httpOpts);
}


export async function serverRequestGetBlob(apiUrl, store) {
  if (!store || !apiUrl) return Promise.reject('Required parameters missing.');
  const httpOpts = {
    credentials: 'include',
    method: 'GET',
    ignoreNotFound: true,
    headers: {},
  };
  try {
    const res = await store.dispatch(fetchM(apiUrl, httpOpts));
    if (!res || !res.ok) return null;
    return await res.blob();
  } catch (e) {
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/actions/fetchM.js